import { keepPreviousData } from '@tanstack/react-query';
import clsx from 'clsx';
import { Ban, Building2, Check, CheckCircle2, FileSpreadsheet, FileUp, Plus, Printer, RotateCcw, Save, Search, Trash2, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import {
  addMonths,
  amountLineLabel,
  BILLING_TYPES,
  hasPaymentTerms,
  monthEnd,
  monthStart,
  paymentBreakdown,
  type AbschlagHeader,
  type AbschlagModel,
  type BillingType,
  type SnHeader,
  type SnModel,
  type SnSettings,
} from '@pz/core';
import { PaymentBreakdownTable, PaymentFields, paymentFormOf, paymentTermsOf, type PaymentForm } from '../components/customers';
import { StepUpSettings } from '../components/step-up';
import { HoursInput, LocationChecks } from '../components/entry';
import {
  Button,
  Checkbox,
  ConfirmModal,
  EmptyState,
  ErrorBox,
  Field,
  Input,
  Loading,
  Modal,
  Notice,
  PageHeader,
  Panel,
  Pill,
  Select,
  SortTh,
  Textarea,
  td,
  th,
  useToast,
  type Tone,
} from '../components/ui';
import { useTableSort } from '../lib/sort';
import { saveFile } from '../lib/download';
import { d, h, hh, money, month, pct } from '../lib/format';
import { useDebounced, useM, useQ } from '../lib/hooks';
import { useMe } from '../lib/me';
import { SnPrint } from '../components/sn-print';
import { ScrollTable, Sentinel, useChunked } from '../components/virtual';
import { rpc, RpcError } from '../lib/rpc';
import { lang } from '../lib/format';
import type { ProcOutput } from '@pz/server';
import { useSticky } from '../lib/sticky';

const DOC_TONE: Record<string, Tone> = { draft: 'neutral', final: 'accent', invoiced: 'info', paid: 'good', cancelled: 'bad' };

export function parseMoney(input: string): number | null {
  const s = input.replace(/[€\s]/g, '');
  if (!s) return null;
  const normalized = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
  const n = Number(normalized);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

export function MoneyInput({ value, onChange, className }: { value: number | null; onChange: (cents: number | null) => void; className?: string }) {
  const fmt = (v: number | null) => (v === null ? '' : (v / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(fmt(value));
  useEffect(() => {
    if (!focused) setText(fmt(value));
  }, [value, focused]);
  return (
    <Input
      inputMode="decimal"
      className={clsx('num text-right', className)}
      value={text}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parseMoney(e.target.value));
      }}
    />
  );
}

/* ------------------------------------------------------------------ */
/* List                                                                 */
/* ------------------------------------------------------------------ */

function CreateDocModal({ kind, onClose }: { kind: 'stundennachweis' | 'abschlag' | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { me, can, isLead } = useMe();
  const navigate = useNavigate();
  const projects = useQ('projects.list', {}, { enabled: !!kind });
  const prev = addMonths(monthStart(me.today), -1);
  const [projectId, setProjectId] = useState('');
  const [from, setFrom] = useState(prev);
  const [to, setTo] = useState(monthEnd(prev));
  const [asOf, setAsOf] = useState(me.today);
  const createSn = useM('billing.createSn', { invalidate: ['billing.'] });
  const createAb = useM('billing.createAbschlag', { invalidate: ['billing.'] });
  const list = (projects.data?.projects ?? []).filter(
    (p) => !p.archived && (can('billing.manage') || isLead(p.id)) && (kind !== 'abschlag' || p.billingMode !== 'hourly'),
  );
  const months = Array.from({ length: 6 }, (_, i) => addMonths(monthStart(me.today), -i));
  const busy = createSn.isPending || createAb.isPending;
  return (
    <Modal
      open={!!kind}
      onClose={onClose}
      title={kind === 'abschlag' ? t('billing.newAbschlagTitle') : t('billing.newSnTitle')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!projectId}
            loading={busy}
            onClick={async () => {
              const r = kind === 'abschlag' ? await createAb.mutateAsync({ projectId, asOf }) : await createSn.mutateAsync({ projectId, from, to });
              onClose();
              navigate(`/billing/${r.id}`);
            }}
          >
            {t('common.create')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t('common.project')}>
          <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">{t('common.select')}</option>
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.number} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        {kind === 'stundennachweis' ? (
          <>
            <div className="flex flex-wrap gap-1">
              {months.map((m) => (
                <Button
                  key={m}
                  size="sm"
                  variant={from === m && to === monthEnd(m) ? 'primary' : 'secondary'}
                  onClick={() => {
                    setFrom(m);
                    setTo(monthEnd(m));
                  }}
                >
                  {month(m)}
                </Button>
              ))}
            </div>
            <div className="flex gap-4">
              <Field label={t('common.from')}>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </Field>
              <Field label={t('common.to')}>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </Field>
            </div>
          </>
        ) : (
          <Field label={t('billing.fields.asOf')}>
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-44" />
          </Field>
        )}
        <ErrorBox error={createSn.error ?? createAb.error} />
      </div>
    </Modal>
  );
}

export function BillingPage() {
  const { can } = useMe();
  const { t } = useTranslation();
  // Abrechnung: people with the right "Abrechnung" (e.g. assistant of the management); project leads approve only
  if (!can('billing.manage')) return <EmptyState>{t('billing.noAccess')}</EmptyState>;
  return <BillingList />;
}

function BillingList() {
  const { t } = useTranslation();
  const { can } = useMe();
  const [kind, setKind] = useState<'stundennachweis' | 'abschlag' | null>(null);
  // filters stay when the person opens a document and comes back; the search is done by the server (hundreds of projects)
  const [status, setStatus] = useSticky('billing.status', '');
  const [search, setSearch] = useSticky('billing.search', '');
  const term = useDebounced(search.trim());
  const docs = useQ('billing.list', { status: status || undefined, search: term || undefined }, { placeholderData: keepPreviousData });
  const unbilled = useQ('billing.unbilled', {});
  const seeMoney = can('rates.sales.view');
  type DocRow = NonNullable<typeof docs.data>['docs'][number];
  const sorting = useTableSort<DocRow>('billing.docs', {
    number: (x) => x.number,
    kind: (x) => x.kind,
    project: (x) => x.projectNumber,
    period: (x) => x.periodFrom,
    hours: (x) => x.totalMinutes,
    amount: (x) => x.totalCents,
    status: (x) => x.status,
  });
  const docRows = useMemo(() => sorting.apply(docs.data?.docs ?? []), [docs.data, sorting.sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const docChunk = useChunked(docRows, 100, `${status}|${term}|${sorting.sort?.key}|${sorting.sort?.dir}`);
  const unbilledChunk = useChunked(unbilled.data?.rows ?? [], 100);
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('billing.title')}
        subtitle={t('billing.subtitle')}
        actions={
          <>
            <Button icon={<Plus className="size-4" />} onClick={() => setKind('stundennachweis')}>
              {t('billing.newSn')}
            </Button>
            <Button icon={<Plus className="size-4" />} onClick={() => setKind('abschlag')}>
              {t('billing.newAbschlag')}
            </Button>
          </>
        }
      />
      <div className="mb-4">
        <StepUpSettings />
      </div>
      <Panel
        title={t('billing.documents')}
        bodyClassName="p-0"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-faint" />
              <Input className="w-56 pl-8" type="search" placeholder={t('billing.search')} aria-label={t('billing.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t('common.status')}>
              <option value="">{t('common.all')}</option>
              {(['draft', 'final', 'invoiced', 'paid', 'cancelled'] as const).map((s) => (
                <option key={s} value={s}>
                  {t(`billing.docStatus.${s}`)}
                </option>
              ))}
            </Select>
          </div>
        }
      >
        {docs.isLoading && <Loading />}
        <ErrorBox error={docs.error} />
        {docs.data && docs.data.docs.length === 0 && <div className="p-4 text-sm text-graphite">{term || status ? t('billing.noMatch') : t('billing.empty')}</div>}
        {!!docs.data?.docs.length && (
          <ScrollTable bare label={t('billing.documents')} maxHeight="min(60vh, 40rem)">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="border-b border-line bg-sheet-2">
                <tr>
                  <SortTh label={t('billing.number')} sortKey="number" sort={sorting.sort} onSort={sorting.toggle} />
                  <SortTh label={t('common.type')} sortKey="kind" sort={sorting.sort} onSort={sorting.toggle} />
                  <SortTh label={t('common.project')} sortKey="project" sort={sorting.sort} onSort={sorting.toggle} />
                  <SortTh label={t('common.period')} sortKey="period" sort={sorting.sort} onSort={sorting.toggle} />
                  <SortTh label={t('common.hours')} sortKey="hours" sort={sorting.sort} onSort={sorting.toggle} right />
                  <SortTh label={t('billing.amount')} sortKey="amount" sort={sorting.sort} onSort={sorting.toggle} right />
                  <SortTh label={t('common.status')} sortKey="status" sort={sorting.sort} onSort={sorting.toggle} />
                </tr>
              </thead>
              <tbody>
                {docChunk.visible.map((x) => (
                  <tr key={x.id} className="border-b border-line/70 hover:bg-sheet-2">
                    <td className={td}>
                      <Link to={`/billing/${x.id}`} className="num font-semibold text-accent hover:underline">
                        {x.number ?? t('billing.docStatus.draft')}
                      </Link>
                    </td>
                    <td className={td}>{t(`billing.kinds.${x.kind as 'abschlag'}`)}</td>
                    <td className={td}>
                      <span className="num">{x.projectNumber}</span> {x.projectName}
                    </td>
                    <td className={clsx(td, 'num')}>{x.periodFrom ? `${d(x.periodFrom)} - ${d(x.periodTo)}` : '-'}</td>
                    <td className={clsx(td, 'num text-right')}>{x.totalMinutes !== null ? h(x.totalMinutes) : '-'}</td>
                    <td className={clsx(td, 'num text-right')}>{x.totalCents !== null ? money(x.totalCents) : '-'}</td>
                    <td className={td}>
                      <span className="flex flex-wrap gap-1">
                        <Pill tone={DOC_TONE[x.status]}>{t(`billing.docStatus.${x.status as 'draft'}`)}</Pill>
                        {x.basisChanged.length > 0 && (
                          <Pill tone="warn" title={x.basisChanged.map((b) => t(`billing.basis.parts.${b}`)).join(', ')}>
                            <TriangleAlert className="size-3" />
                            {t('billing.basis.changedShort')}
                          </Pill>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {docChunk.hasMore && <Sentinel onMore={docChunk.more} />}
          </ScrollTable>
        )}
        {docs.data?.capped && <p className="border-t border-line px-4 py-2 text-xs text-graphite">{t('billing.listCapped', { count: docs.data.docs.length })}</p>}
      </Panel>

      <Panel title={t('billing.unbilled')} bodyClassName="p-0">
        <p className="px-4 pt-3 text-sm text-graphite">{t('billing.unbilledHint')}</p>
        {unbilled.data && unbilled.data.rows.length === 0 && <div className="p-4 text-sm text-graphite">{t('billing.unbilledNone')}</div>}
        {!!unbilled.data?.rows.length && (
          <ScrollTable bare label={t('billing.unbilled')} maxHeight="min(50vh, 32rem)">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="border-b border-line">
                <tr>
                  <th className={th}>{t('common.project')}</th>
                  <th className={th}>{t('common.month')}</th>
                  <th className={clsx(th, 'text-right')}>{t('common.hours')}</th>
                  {seeMoney && <th className={clsx(th, 'text-right')}>{t('billing.amount')}</th>}
                </tr>
              </thead>
              <tbody>
                {unbilledChunk.visible.map((r) => (
                  <tr key={`${r.projectId}${r.month}`} className="border-b border-line/70">
                    <td className={td}>
                      <span className="num">{r.number}</span> {r.name}
                    </td>
                    <td className={td}>{month(`${r.month}-01`)}</td>
                    <td className={clsx(td, 'num text-right')}>{h(r.minutes)}</td>
                    {seeMoney && <td className={clsx(td, 'num text-right')}>{money(r.valueCents)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {unbilledChunk.hasMore && <Sentinel onMore={unbilledChunk.more} />}
          </ScrollTable>
        )}
      </Panel>
      <CreateDocModal kind={kind} onClose={() => setKind(null)} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Document                                                             */
/* ------------------------------------------------------------------ */

type Doc = NonNullable<ReturnType<typeof useDocQuery>['data']>;
function useDocQuery(id: string) {
  return useQ('billing.get', { id });
}

export function BillingDocPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const q = useDocQuery(id);
  const [confirm, setConfirm] = useState<'finalize' | 'cancel' | 'delete' | null>(null);
  const [preview, setPreview] = useState(false);
  const inv = { invalidate: ['billing.', 'time.', 'kpi.'] };
  const finalize = useM('billing.finalize', inv);
  const setStatus = useM('billing.setStatus', inv);
  const cancel = useM('billing.cancel', inv);
  const del = useM('billing.deleteDraft', inv);
  const importEdit = useM('billing.importEdit', inv);
  const ack = useM('billing.acknowledgeBasis', inv);
  const takeOver = useM('billing.update', inv);
  const [editFile, setEditFile] = useState<{ name: string; base64: string } | null>(null);
  const [editPreview, setEditPreview] = useState<ImportPreview | null>(null);
  const [exporting, setExporting] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const { doc, project, editable } = q.data;
  // day texts edited by hand whose bookings changed afterwards (shown before finalizing)
  const outdated = q.data.model.kind === 'stundennachweis' ? q.data.model.days.filter((x) => x.textOutdated).length : 0;

  // edit in Excel (item 14): export the draft, edit the yellow columns, load the file back after a preview
  const exportEdit = async () => {
    setExporting(true);
    try {
      saveFile(await rpc('billing.exportEdit', { id, lang: lang() }));
    } catch (e) {
      toast.error(e);
    } finally {
      setExporting(false);
    }
  };
  const pickEditFile = async (file: File) => {
    const base64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    });
    setEditFile({ name: file.name, base64 });
    setEditPreview(null);
    try {
      setEditPreview(await importEdit.mutateAsync({ id, base64, apply: false }));
    } catch {
      // shown in the dialog
    }
  };
  const basis = q.data.basis;
  const exportFile = async () => {
    setExporting(true);
    try {
      saveFile(await rpc('billing.export', { id }));
    } catch (e) {
      toast.error(e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 print:hidden">
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {t(`billing.kinds.${doc.kind as 'abschlag'}`)} {doc.number && <span className="num">{doc.number}</span>}
            <Pill tone={DOC_TONE[doc.status]}>{t(`billing.docStatus.${doc.status as 'draft'}`)}</Pill>
          </span>
        }
        subtitle={
          <>
            <span className="num">{project.number}</span> {project.name}
            {doc.periodFrom && ` · ${d(doc.periodFrom)} - ${d(doc.periodTo)}`}
          </>
        }
        actions={
          <>
            {q.data.model.kind === 'stundennachweis' && (
              <Button icon={<Printer className="size-4" />} onClick={() => (__DEMO__ ? setPreview(true) : window.print())} title={t('billing.printHint')}>
                {t('billing.print')}
              </Button>
            )}
            <Button icon={<FileSpreadsheet className="size-4" />} loading={exporting} onClick={exportFile}>
              {t('common.exportExcel')}
            </Button>
            {doc.status === 'draft' && (
              <>
                <Button icon={<FileSpreadsheet className="size-4" />} onClick={exportEdit} title={t('billing.edit.exportHint')}>
                  {t('billing.edit.export')}
                </Button>
                <label className="inline-flex">
                  <input
                    type="file"
                    accept=".xlsx"
                    className="sr-only"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) pickEditFile(f).catch(toast.error);
                    }}
                  />
                  <span className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-line bg-sheet px-3 py-1.5 text-sm font-medium hover:bg-sheet-2" role="button" tabIndex={0}>
                    <FileUp className="size-4" />
                    {t('billing.edit.import')}
                  </span>
                </label>
                <Button variant="danger" icon={<Trash2 className="size-4" />} onClick={() => setConfirm('delete')}>
                  {t('billing.deleteDraft')}
                </Button>
                <Button variant="primary" icon={<CheckCircle2 className="size-4" />} onClick={() => setConfirm('finalize')}>
                  {t('billing.finalize')}
                </Button>
              </>
            )}
            {doc.status === 'final' && (
              <Button onClick={() => setStatus.mutateAsync({ id, status: 'invoiced' }).catch(toast.error)}>{t('billing.markInvoiced')}</Button>
            )}
            {(doc.status === 'final' || doc.status === 'invoiced') && (
              <>
                <Button onClick={() => setStatus.mutateAsync({ id, status: 'paid' }).catch(toast.error)}>{t('billing.markPaid')}</Button>
                <Button variant="danger" icon={<Ban className="size-4" />} onClick={() => setConfirm('cancel')}>
                  {t('billing.cancel')}
                </Button>
              </>
            )}
          </>
        }
      />
      {!editable && <Notice tone="neutral">{t('billing.readOnly')}</Notice>}
      {editable && basis.changed.length > 0 && (
        <Notice tone="warn">
          <div className="flex flex-col gap-2">
            <span>{t('billing.basis.changed', { parts: basis.changed.map((b) => t(`billing.basis.parts.${b}`)).join(', ') })}</span>
            <span className="flex flex-wrap gap-2">
              {basis.changed.includes('customer') && basis.recipient && (
                <Button
                  size="sm"
                  icon={<Building2 className="size-3.5" />}
                  loading={takeOver.isPending}
                  onClick={() => takeOver.mutateAsync({ id, version: doc.version, header: { ...basis.recipient } }).catch(toast.error)}
                >
                  {t('billing.basis.takeRecipient')}
                </Button>
              )}
              {basis.changed.includes('project') && basis.project && (
                <Button
                  size="sm"
                  loading={takeOver.isPending}
                  onClick={() => takeOver.mutateAsync({ id, version: doc.version, header: { projectNumber: basis.project!.number, projectLabel: basis.project!.name } }).catch(toast.error)}
                >
                  {t('billing.basis.takeProject')}
                </Button>
              )}
              <Button
                size="sm"
                icon={<Check className="size-3.5" />}
                loading={ack.isPending}
                onClick={() =>
                  basis.token &&
                  ack
                    .mutateAsync({ id, token: basis.token })
                    .then(() => toast.ok(t('billing.basis.checked')))
                    .catch(toast.error)
                }
              >
                {t('billing.basis.markChecked')}
              </Button>
            </span>
          </div>
        </Notice>
      )}
      {doc.status === 'cancelled' && doc.cancelReason && <Notice tone="bad">{doc.cancelReason}</Notice>}
      </div>
      {q.data.model.kind === 'stundennachweis' ? <SnEditor data={q.data} preview={preview} onClosePreview={() => setPreview(false)} /> : <AbschlagEditor data={q.data} />}

      <EditImportModal
        file={editFile}
        preview={editPreview}
        error={importEdit.error}
        busy={importEdit.isPending}
        onClose={() => (setEditFile(null), setEditPreview(null))}
        onApply={() =>
          editFile &&
          editPreview &&
          importEdit
            .mutateAsync({ id, base64: editFile.base64, apply: true, version: editPreview.version, previewToken: editPreview.previewToken })
            .then((r) => {
              toast.ok(t('billing.edit.applied', { count: r.changes }));
              setEditFile(null);
              setEditPreview(null);
            })
            .catch(async (e) => {
              // the system data changed since the preview: show the new preview instead of applying old changes
              if (e instanceof RpcError && e.code === 'CONFLICT') {
                try {
                  setEditPreview(await importEdit.mutateAsync({ id, base64: editFile.base64, apply: false }));
                  toast.ok(t('billing.edit.previewRefreshed'));
                } catch {
                  // shown in the dialog
                }
              }
            })
        }
      />
      <ConfirmModal
        open={confirm === 'finalize'}
        onClose={() => setConfirm(null)}
        title={t('billing.finalizeTitle')}
        text={outdated ? `${t('billing.finalizeText')} ${t('billing.finalizeOutdated', { count: outdated })}` : t('billing.finalizeText')}
        confirmLabel={t('billing.finalize')}
        loading={finalize.isPending}
        error={finalize.error}
        onConfirm={() =>
          finalize
            .mutateAsync({ id, version: doc.version })
            .then((r) => {
              toast.ok(t('billing.finalized', { number: r.number }));
              setConfirm(null);
            })
            .catch(() => {})
        }
      />
      <ConfirmModal
        open={confirm === 'cancel'}
        onClose={() => setConfirm(null)}
        title={t('billing.cancelTitle')}
        text={t('billing.cancelText')}
        confirmLabel={t('billing.cancel')}
        danger
        reason="required"
        loading={cancel.isPending}
        error={cancel.error}
        onConfirm={(reason) =>
          cancel
            .mutateAsync({ id, reason })
            .then(() => setConfirm(null))
            .catch(() => {})
        }
      />
      <ConfirmModal
        open={confirm === 'delete'}
        onClose={() => setConfirm(null)}
        title={t('billing.deleteDraft')}
        confirmLabel={t('common.delete')}
        danger
        loading={del.isPending}
        error={del.error}
        onConfirm={() =>
          del
            .mutateAsync({ id, version: doc.version })
            .then(() => navigate('/billing'))
            .catch(() => {})
        }
      />
    </div>
  );
}

type ImportPreview = ProcOutput<'billing.importEdit'>;

/** Recipient of the document from the customer data of the project (Kunden): firm, address and contact person. */
function RecipientButton({ projectId, onPick }: { projectId: string; onPick: (r: { customer: string; customerAddress: string; customerContact: string }) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const q = useQ('billing.recipientChoices', { projectId }, { enabled: open });
  const [contact, setContact] = useState<Record<string, string>>({});
  return (
    <>
      <Button size="sm" icon={<Building2 className="size-3.5" />} onClick={() => setOpen(true)}>
        {t('billing.recipient.button')}
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title={t('billing.recipient.title')} footer={<Button onClick={() => setOpen(false)}>{t('common.close')}</Button>}>
        {q.isLoading && <Loading />}
        <ErrorBox error={q.error} />
        {q.data && q.data.choices.length === 0 && <EmptyState>{t('billing.recipient.none')}</EmptyState>}
        <ul className="flex flex-col gap-2">
          {(q.data?.choices ?? []).map((c) => {
            const chosen = contact[c.companyId] ?? c.contacts.find((x) => x.roles.includes('invoice'))?.id ?? '';
            return (
              <li key={c.companyId} className="rounded-md border border-line p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{c.name}</span>
                  {c.agreed && <Pill tone="accent">{t('billing.recipient.agreed')}</Pill>}
                  {c.roles.map((r) => (
                    <Pill key={r}>{t(`customers.roles.${r}` as 'customers.roles.auftraggeber', { defaultValue: r })}</Pill>
                  ))}
                </div>
                {c.address && <p className="mt-1 text-sm whitespace-pre-line text-graphite">{c.address}</p>}
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  {c.contacts.length > 0 && (
                    <Field label={t('billing.fields.customerContact')}>
                      <Select className="w-64" value={chosen} onChange={(e) => setContact({ ...contact, [c.companyId]: e.target.value })}>
                        <option value="">-</option>
                        {c.contacts.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name}
                            {x.position ? ` (${x.position})` : ''}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  )}
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      onPick({ customer: c.name, customerAddress: c.address, customerContact: c.contacts.find((x) => x.id === chosen)?.name ?? '' });
                      setOpen(false);
                    }}
                  >
                    {t('billing.recipient.take')}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </Modal>
    </>
  );
}

/** Preview of an edited Excel file before it changes the draft (nothing is written before "Übernehmen"). */
function EditImportModal({
  file,
  preview,
  error,
  busy,
  onClose,
  onApply,
}: {
  file: { name: string } | null;
  preview: ImportPreview | null;
  error: unknown;
  busy: boolean;
  onClose: () => void;
  onApply: () => void;
}) {
  const { t } = useTranslation();
  const p = preview;
  return (
    <Modal
      open={!!file}
      wide
      onClose={onClose}
      title={t('billing.edit.previewTitle')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!p || p.changes === 0} loading={busy} onClick={onApply}>
            {t('billing.edit.apply', { count: p?.changes ?? 0 })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {file && <p className="text-graphite">{file.name}</p>}
        {busy && !p && <Loading />}
        <ErrorBox error={error} />
        {p && (
          <>
            {p.stale && <Notice tone="warn">{t('billing.edit.stale')}</Notice>}
            {p.changes === 0 && <Notice tone="neutral">{t('billing.edit.noChanges')}</Notice>}
            {p.header.length > 0 && (
              <section>
                <h3 className="mb-1 font-semibold">{t('billing.header')}</h3>
                <ul className="flex flex-col gap-0.5">
                  {p.header.map((c) => (
                    <li key={c.field}>
                      <span className="text-graphite">{t(`billing.fields.${c.field}` as 'billing.fields.title', { defaultValue: c.field })}:</span> <s className="text-faint">{c.from || '-'}</s> → {c.to || '-'}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {p.entries.length > 0 && (
              <section>
                <h3 className="mb-1 font-semibold">{t('billing.edit.entries', { count: p.entries.length })}</h3>
                <ul className="flex max-h-56 flex-col gap-0.5 overflow-y-auto">
                  {p.entries.map((e) => (
                    <li key={e.entryId}>
                      <span className="num">{d(e.date)}</span> {e.person} · {e.task}: {e.from.excluded ? t('billing.edit.excluded') : `${h(e.from.minutes)} h`} →{' '}
                      {e.to.excluded ? t('billing.edit.excluded') : `${h(e.to.minutes)} h`}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {p.days.length > 0 && <p>{t('billing.edit.days', { count: p.days.length })}</p>}
            {p.phases.length > 0 && (
              <section>
                <h3 className="mb-1 font-semibold">{t('billing.edit.phases')}</h3>
                <ul className="flex flex-col gap-0.5">
                  {p.phases.map((x) => (
                    <li key={x.key}>
                      {x.label}: {pct(x.from)} → {pct(x.to)}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {p.extras && <p>{t('billing.edit.extras', { count: p.extras.to.length })}</p>}
            {p.problems.length > 0 && (
              <Notice tone="warn">
                {t('billing.edit.problems', { count: p.problems.length })}
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {p.problems.slice(0, 20).map((x, i) => (
                    <li key={i}>
                      {t(`billing.edit.problem.${x.code}` as 'billing.edit.problem.own')}
                      {x.sheet ? ` (${x.sheet}, ${t('billing.edit.row', { row: x.row })})` : ''}
                    </li>
                  ))}
                </ul>
              </Notice>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

const SN_FIELDS: (keyof SnHeader)[] = [
  'title',
  'customer',
  'customerAddress',
  'customerContact',
  'projectLabel',
  'projectNumber',
  'customerReference',
  'consultant',
  'partner',
  'periodLabel',
  'siteLocation',
  'place',
  'documentDate',
  'providerSignatureName',
  'clientSignatureName',
  'issuerName',
  'issuerAddress',
  'note',
];

function SnEditor({ data, preview, onClosePreview }: { data: Doc; preview: boolean; onClosePreview: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const model = data.model as SnModel;
  const doc = data.doc;
  const editable = data.editable;
  const [header, setHeader] = useState<SnHeader>(model.header);
  const [dayNotes, setDayNotes] = useState<Record<string, string>>(doc.dayNotes as Record<string, string>);
  const [overrides, setOverrides] = useState<Record<string, { billedMinutes?: number | null; excluded?: boolean }>>(
    doc.overrides as Record<string, { billedMinutes?: number | null; excluded?: boolean }>,
  );
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setHeader(model.header);
    setDayNotes(doc.dayNotes as Record<string, string>);
    setOverrides(doc.overrides as Record<string, { billedMinutes?: number | null; excluded?: boolean }>);
    setDirty(false);
  }, [doc.version, model.header, doc.dayNotes, doc.overrides]);
  const update = useM('billing.update', { invalidate: ['billing.get'] });
  const save = async (extra: { settings?: Partial<SnSettings>; dayNotesChecked?: string[] } = {}) => {
    try {
      await update.mutateAsync({ id: doc.id, version: doc.version, header: header as unknown as Record<string, unknown>, dayNotes, overrides, ...extra });
      setDirty(false);
      if (!extra.settings && !extra.dayNotesChecked) toast.ok(t('common.saved'));
    } catch (e) {
      toast.error(e);
    }
  };
  const s = model.settings;
  const setSetting = (patch: Partial<SnSettings>) => save({ settings: patch });
  const persons = model.persons;
  const showRows = useMemo(() => model.rows, [model.rows]);
  const savedNotes = doc.dayNotes as Record<string, string>;
  // flagged only while the text shown is the saved one (a new edit or a reset settles it on saving)
  const isOutdated = (day: SnModel['days'][number]) => day.textOutdated && dayNotes[day.date] !== undefined && dayNotes[day.date] === savedNotes[day.date];
  const outdatedCount = editable ? model.days.filter(isOutdated).length : 0;

  return (
    <>
    <SnPrint model={model} header={header} dayNotes={dayNotes} />
    <Modal
      open={preview}
      wide
      onClose={onClosePreview}
      title={t('billing.printPreview')}
      footer={
        <>
          <Button onClick={onClosePreview}>{t('common.close')}</Button>
          <Button variant="primary" icon={<Printer className="size-4" />} onClick={() => window.print()}>
            {t('billing.print')}
          </Button>
        </>
      }
    >
      <Notice tone="warn" className="mb-3">
        {t('common.demoPrint')}
      </Notice>
      <div className="overflow-x-auto rounded-md border border-line bg-white p-4">
        <SnPrint model={model} header={header} dayNotes={dayNotes} screen />
      </div>
    </Modal>
    <div className="flex flex-col gap-4 print:hidden">
      <div className={clsx('grid items-start gap-4', editable && 'xl:grid-cols-[minmax(0,1fr)_300px]')}>
        <Panel
          title={t('billing.header')}
          actions={
            editable && (
              <span className="flex gap-2">
                <RecipientButton projectId={data.project.id} onPick={(r) => (setHeader({ ...header, ...r }), setDirty(true))} />
                <Button size="sm" variant={dirty ? 'primary' : 'secondary'} icon={<Save className="size-3.5" />} loading={update.isPending} onClick={() => save()}>
                  {t('common.save')}
                </Button>
              </span>
            )
          }
        >
          <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
            {SN_FIELDS.map((f) => (
              <Field key={f} label={t(`billing.fields.${f}`)} className={f === 'note' || f === 'customerAddress' ? 'sm:col-span-2 2xl:col-span-3' : undefined}>
                {f === 'note' || f === 'customerAddress' ? (
                  <Textarea
                    rows={2}
                    disabled={!editable}
                    value={header[f] ?? ''}
                    onChange={(e) => {
                      setHeader({ ...header, [f]: e.target.value });
                      setDirty(true);
                    }}
                  />
                ) : (
                  <Input
                    type={f === 'documentDate' ? 'date' : 'text'}
                    disabled={!editable}
                    value={header[f] ?? ''}
                    onChange={(e) => {
                      setHeader({ ...header, [f]: e.target.value });
                      setDirty(true);
                    }}
                  />
                )}
              </Field>
            ))}
          </div>
        </Panel>
        {editable && (
          <Panel title={t('billing.options')}>
            <div className="flex flex-col gap-3">
              <Field label={t('billing.layout.label')}>
                <Select value={s.layout} onChange={(e) => setSetting({ layout: e.target.value as 'matrix' })}>
                  <option value="matrix">{t('billing.layout.matrix')}</option>
                  <option value="list">{t('billing.layout.list')}</option>
                </Select>
              </Field>
              <Field label={t('billing.includeTypes')}>
                <div className="flex flex-col gap-1">
                  {BILLING_TYPES.map((b) => (
                    <Checkbox
                      key={b}
                      label={t(`billingType.${b}`)}
                      checked={s.includeTypes.includes(b)}
                      onChange={(e) =>
                        setSetting({ includeTypes: e.target.checked ? [...s.includeTypes, b] : s.includeTypes.filter((x: BillingType) => x !== b) })
                      }
                    />
                  ))}
                </div>
              </Field>
              <Field label={t('billing.language')}>
                <Select value={s.language} onChange={(e) => setSetting({ language: e.target.value as 'de' })}>
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                </Select>
              </Field>
              <Field label={t('billing.dayTextPrefix.label')}>
                <Select value={s.dayTextPrefix} onChange={(e) => setSetting({ dayTextPrefix: e.target.value as 'name' })}>
                  <option value="code">{t('billing.dayTextPrefix.code')}</option>
                  <option value="discipline">{t('billing.dayTextPrefix.discipline')}</option>
                  <option value="name">{t('billing.dayTextPrefix.name')}</option>
                  <option value="none">{t('billing.dayTextPrefix.none')}</option>
                </Select>
              </Field>
              <Field label={t('billing.dayTextTask.label')}>
                <Select value={s.dayTextTask ?? 'fallback'} onChange={(e) => setSetting({ dayTextTask: e.target.value as 'always' })}>
                  <option value="fallback">{t('billing.dayTextTask.fallback')}</option>
                  <option value="always">{t('billing.dayTextTask.always')}</option>
                </Select>
              </Field>
              <Field label={t('billing.dayTextLocations')} hint={t('billing.dayTextHint')}>
                <LocationChecks value={s.dayTextLocations ?? []} onChange={(v) => setSetting({ dayTextLocations: v })} />
              </Field>
              {data.canSeeAmounts && <Checkbox label={t('billing.showAmounts')} checked={s.showAmounts} onChange={(e) => setSetting({ showAmounts: e.target.checked })} />}
              <Checkbox label={t('billing.includeUnapproved')} checked={s.includeUnapproved} onChange={(e) => setSetting({ includeUnapproved: e.target.checked })} />
            </div>
          </Panel>
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-4">
        {model.unapprovedCount > 0 && <Notice tone="warn">{t('billing.unapprovedWarning', { count: model.unapprovedCount })}</Notice>}
        {model.entryCount === 0 && <Notice tone="neutral">{t('billing.noEntries')}</Notice>}
        {outdatedCount > 0 && <Notice tone="warn">{t('billing.outdatedNotice', { count: outdatedCount })}</Notice>}
        <Panel title={t('billing.preview')} bodyClassName="p-0">
          <div className="border-b border-line px-5 py-4">
            {header.issuerName && <div className="text-xs text-graphite">{[header.issuerName, header.issuerAddress].filter(Boolean).join(', ')}</div>}
            <div className="text-lg font-semibold text-accent">{header.title}</div>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-sm">
              {(['customer', 'customerAddress', 'customerContact', 'projectLabel', 'customerReference', 'consultant', 'partner', 'periodLabel', 'siteLocation'] as const)
                .filter((f) => header[f])
                .map((f) => (
                  <div key={f} className="contents">
                    <dt className="text-graphite">{t(`billing.fields.${f}`)}</dt>
                    <dd className="whitespace-pre-line">
                      {header[f]}
                      {f === 'projectLabel' && header.projectNumber ? ` (${header.projectNumber})` : ''}
                    </dd>
                  </div>
                ))}
            </dl>
          </div>
          {s.layout === 'matrix' ? (
            <div className="scroll-x">
              <table className="w-full min-w-[720px] text-[13px]">
                <thead className="border-b border-line bg-sheet-2">
                  <tr>
                    <th className={th}>{t('common.date')}</th>
                    {persons.map((p) => (
                      <th key={p.id} className={clsx(th, 'text-right')}>
                        {p.name}
                        {p.discipline && <span className="block font-normal">{p.discipline}</span>}
                      </th>
                    ))}
                    <th className={clsx(th, 'text-right')}>{t('common.total')}</th>
                    <th className={clsx(th, 'w-[46%] min-w-80')}>{t('billing.dayText')}</th>
                  </tr>
                </thead>
                <tbody>
                  {model.days.map((day) => {
                    const custom = dayNotes[day.date];
                    const text = custom !== undefined ? custom : day.generatedText;
                    const empty = !day.totalMinutes && custom === undefined;
                    const outdated = editable && isOutdated(day);
                    return (
                      <tr key={day.date} className={clsx('border-b border-line/70', day.holiday ? 'hatch' : day.weekend && 'bg-sheet-2')}>
                        <td className={clsx(td, 'whitespace-nowrap')}>{day.label}</td>
                        {persons.map((p) => (
                          <td key={p.id} className={clsx(td, 'num text-right')}>
                            {day.minutes[p.id] ? h(day.minutes[p.id]) : ''}
                          </td>
                        ))}
                        <td className={clsx(td, 'num text-right font-semibold')}>{day.totalMinutes ? h(day.totalMinutes) : ''}</td>
                        <td className={clsx(td, 'py-1')}>
                          {editable && !empty ? (
                            <div className="flex flex-col gap-0.5">
                              <Textarea
                                rows={Math.min(8, Math.max(1, text.split('\n').length + Math.floor(text.length / 140)))}
                                className={clsx('min-h-0 text-[13px]', outdated && 'border-warn')}
                                value={text}
                                aria-label={`${t('billing.dayText')} ${day.label}`}
                                onChange={(e) => {
                                  setDayNotes({ ...dayNotes, [day.date]: e.target.value });
                                  setDirty(true);
                                }}
                              />
                              {outdated && (
                                <div className="rounded-md border border-warn/40 bg-warn-soft/40 px-2 py-1.5 text-xs">
                                  <div className="flex items-center gap-1 font-semibold text-warn">
                                    <TriangleAlert className="size-3.5" aria-hidden /> {t('billing.dayTextOutdated')}
                                  </div>
                                  <div className="mt-0.5 text-graphite">{t('billing.dayTextOutdatedHint')}</div>
                                  <div className="mt-0.5 whitespace-pre-wrap">{day.generatedText || '-'}</div>
                                  <div className="mt-1 flex flex-wrap gap-3">
                                    <button className="flex items-center gap-1 text-accent" onClick={() => save({ dayNotesChecked: [day.date] })}>
                                      <Check className="size-3" /> {t('billing.dayTextKeep')}
                                    </button>
                                    <button
                                      className="flex items-center gap-1 text-accent"
                                      onClick={() => {
                                        const next = { ...dayNotes };
                                        delete next[day.date];
                                        setDayNotes(next);
                                        setDirty(true);
                                      }}
                                    >
                                      <RotateCcw className="size-3" /> {t('billing.dayTextReset')}
                                    </button>
                                  </div>
                                </div>
                              )}
                              {custom !== undefined && !outdated && (
                                <span className="flex flex-wrap items-center gap-2 text-xs">
                                  <Pill>{t('billing.dayTextEdited')}</Pill>
                                  <button
                                    className="flex items-center gap-1 text-accent"
                                    onClick={() => {
                                      const next = { ...dayNotes };
                                      delete next[day.date];
                                      setDayNotes(next);
                                      setDirty(true);
                                    }}
                                  >
                                    <RotateCcw className="size-3" /> {t('billing.dayTextReset')}
                                  </button>
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="whitespace-pre-wrap text-graphite">{text || day.holiday || ''}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-sheet-2 font-semibold">
                    <td className={td}>{t('common.total')}</td>
                    {persons.map((p) => (
                      <td key={p.id} className={clsx(td, 'num text-right')}>
                        {h(p.minutes)}
                      </td>
                    ))}
                    <td className={clsx(td, 'num text-right')}>{h(model.totalMinutes)}</td>
                    <td className={td} />
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="px-5 py-3 text-sm text-graphite">
              {t('billing.layout.list')}: {model.entryCount} · {hh(model.totalMinutes)}
            </div>
          )}
          {s.showAmounts && model.totalAmountCents !== null && (
            <div className="border-t border-line px-5 py-3">
              <table className="text-sm">
                <tbody>
                  {model.amounts.map((l) => (
                    <tr key={`${l.personId}${l.from}`}>
                      <td className="pr-6">{amountLineLabel(l, model.amounts)}</td>
                      <td className="num pr-6 text-right">{hh(l.minutes)}</td>
                      <td className={clsx('num pr-6 text-right', l.rateCents === null ? 'text-bad' : 'text-graphite')}>{l.rateCents !== null ? money(l.rateCents) : t('billing.noRate')}</td>
                      <td className="num text-right">{l.amountCents !== null ? money(l.amountCents) : '-'}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td className="pr-6">{t('billing.net')}</td>
                    <td />
                    <td />
                    <td className="num text-right">{money(model.totalAmountCents)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title={t('billing.rows')} bodyClassName="p-0">
          <p className="border-b border-line px-4 py-2 text-xs text-graphite">{t('billing.billedHint')}</p>
          <div className="scroll-x">
            <table className="w-full min-w-[760px] text-[13px]">
              <thead className="border-b border-line bg-sheet-2">
                <tr>
                  <th className={th}>{t('common.date')}</th>
                  <th className={th}>{t('common.person')}</th>
                  <th className={th}>{t('common.task')}</th>
                  <th className={th}>{t('common.description')}</th>
                  <th className={clsx(th, 'text-right')}>{t('common.hours')}</th>
                  <th className={th}>{t('billing.billedHours')}</th>
                  <th className={th}>{t('billing.exclude')}</th>
                </tr>
              </thead>
              <tbody>
                {showRows.map((r) => {
                  const o = overrides[r.entryId] ?? {};
                  return (
                    <tr key={r.entryId} className={clsx('border-b border-line/70', (o.excluded ?? r.excluded) && 'text-faint line-through')}>
                      <td className={clsx(td, 'num whitespace-nowrap')}>{d(r.date)}</td>
                      <td className={td}>{r.personName}</td>
                      <td className={td}>{r.taskLabel}</td>
                      <td className={clsx(td, 'max-w-sm whitespace-pre-line')}>
                        {r.description}
                        {r.status !== 'approved' && r.status !== 'billed' && (
                          <Pill tone="warn" className="ml-1">
                            {t(`status.${r.status}`)}
                          </Pill>
                        )}
                      </td>
                      <td className={clsx(td, 'num text-right')}>{h(r.originalMinutes)}</td>
                      <td className={clsx(td, 'py-1')}>
                        {editable ? (
                          <HoursInput
                            placeholder={h(r.originalMinutes)}
                            value={o.billedMinutes ?? (r.overridden ? r.minutes : null)}
                            onChange={(m) => {
                              setOverrides({ ...overrides, [r.entryId]: { ...o, billedMinutes: m } });
                              setDirty(true);
                            }}
                          />
                        ) : (
                          <span className="num">{h(r.minutes)}</span>
                        )}
                      </td>
                      <td className={td}>
                        <Checkbox
                          label={<span className="sr-only">{t('billing.exclude')}</span>}
                          disabled={!editable}
                          checked={!!(o.excluded ?? r.excluded)}
                          onChange={(e) => {
                            setOverrides({ ...overrides, [r.entryId]: { ...o, excluded: e.target.checked } });
                            setDirty(true);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {editable && dirty && (
            <div className="flex justify-end border-t border-line px-4 py-2">
              <Button variant="primary" size="sm" icon={<Save className="size-3.5" />} loading={update.isPending} onClick={() => save()}>
                {t('common.save')}
              </Button>
            </div>
          )}
        </Panel>
      </div>
    </div>
    </>
  );
}

const AB_FIELDS: (keyof AbschlagHeader)[] = [
  'title',
  'sequenceLabel',
  'customer',
  'customerAddress',
  'customerContact',
  'projectLabel',
  'projectNumber',
  'contractNote',
  'asOf',
  'documentDate',
  'place',
  'vatPercent',
  'issuerName',
  'issuerAddress',
  'note',
];

function AbschlagEditor({ data }: { data: Doc }) {
  const { t } = useTranslation();
  const toast = useToast();
  const model = data.model as AbschlagModel;
  const doc = data.doc;
  const editable = data.editable;
  const [header, setHeader] = useState<AbschlagHeader>(model.header);
  const [progress, setProgress] = useState<Record<string, number>>(doc.progress as Record<string, number>);
  const [extras, setExtras] = useState<{ label: string; amountCents: number }[]>(doc.extras as { label: string; amountCents: number }[]);
  const [dirty, setDirty] = useState(false);
  const [payment, setPayment] = useState<PaymentForm>(() => paymentFormOf(model.payment?.terms));
  const [payDirty, setPayDirty] = useState(false);
  useEffect(() => {
    setHeader(model.header);
    setProgress(doc.progress as Record<string, number>);
    setExtras(doc.extras as { label: string; amountCents: number }[]);
    setPayment(paymentFormOf(model.payment?.terms));
    setDirty(false);
    setPayDirty(false);
  }, [doc.version, model.header, model.payment, doc.progress, doc.extras]);
  const update = useM('billing.update', { invalidate: ['billing.get'] });
  const liveTerms = paymentTermsOf(payment);
  const livePay = hasPaymentTerms(liveTerms) ? paymentBreakdown(model.totals.netCents, header.vatPercent || 0, liveTerms) : null;
  const save = () =>
    update
      .mutateAsync({
        id: doc.id,
        version: doc.version,
        header: header as unknown as Record<string, unknown>,
        progress,
        extras,
        payment: payDirty ? (hasPaymentTerms(liveTerms) ? liveTerms : null) : undefined,
      })
      .then(() => {
        setDirty(false);
        setPayDirty(false);
        toast.ok(t('common.saved'));
      })
      .catch(toast.error);
  const noFee = model.totals.feeCents === 0;

  return (
    <div className="flex flex-col gap-4">
      <Panel
        title={t('billing.header')}
        actions={
          editable && (
            <span className="flex gap-2">
              <RecipientButton projectId={data.project.id} onPick={(r) => (setHeader({ ...header, ...r }), setDirty(true))} />
              <Button size="sm" variant={dirty || payDirty ? 'primary' : 'secondary'} icon={<Save className="size-3.5" />} loading={update.isPending} onClick={save}>
                {t('common.save')}
              </Button>
            </span>
          )
        }
      >
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {AB_FIELDS.map((f) => (
            <Field key={f} label={t(`billing.fields.${f}`)} className={f === 'note' || f === 'contractNote' || f === 'customerAddress' ? 'sm:col-span-2' : undefined}>
              {f === 'note' || f === 'contractNote' || f === 'customerAddress' ? (
                <Textarea
                  rows={2}
                  disabled={!editable}
                  value={String(header[f] ?? '')}
                  onChange={(e) => {
                    setHeader({ ...header, [f]: e.target.value });
                    setDirty(true);
                  }}
                />
              ) : (
                <Input
                  type={f === 'asOf' || f === 'documentDate' ? 'date' : f === 'vatPercent' ? 'number' : 'text'}
                  disabled={!editable}
                  value={String(header[f] ?? '')}
                  onChange={(e) => {
                    setHeader({ ...header, [f]: f === 'vatPercent' ? Number(e.target.value) : e.target.value });
                    setDirty(true);
                  }}
                />
              )}
            </Field>
          ))}
        </div>
      </Panel>
      <div className="flex min-w-0 flex-col gap-4">
        {noFee && <Notice tone="warn">{t('billing.noFee')}</Notice>}
        <Panel title={t('billing.phases')} bodyClassName="p-0">
          <div className="scroll-x">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="border-b border-line bg-sheet-2">
                <tr>
                  <th className={th}>{t('billing.phase')}</th>
                  <th className={clsx(th, 'text-right')}>{t('billing.weight')}</th>
                  <th className={clsx(th, 'text-right')}>{t('billing.fee')}</th>
                  <th className={clsx(th, 'text-right')}>{t('billing.progress')}</th>
                  <th className={clsx(th, 'text-right')}>{t('billing.earned')}</th>
                  <th className={clsx(th, 'text-right')}>{t('billing.previous')}</th>
                  <th className={clsx(th, 'text-right')}>{t('billing.current')}</th>
                </tr>
              </thead>
              <tbody>
                {model.lines.map((l) => (
                  <tr key={l.key} className="border-b border-line/70">
                    <td className={td}>{l.label}</td>
                    <td className={clsx(td, 'num text-right')}>{l.weightPercent ?? '-'}</td>
                    <td className={clsx(td, 'num text-right')}>{money(l.feeCents)}</td>
                    <td className={clsx(td, 'py-1 text-right')}>
                      {editable ? (
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          className="num ml-auto w-20 text-right"
                          value={progress[l.key] ?? 0}
                          onChange={(e) => {
                            setProgress({ ...progress, [l.key]: Math.max(0, Math.min(100, Number(e.target.value))) });
                            setDirty(true);
                          }}
                        />
                      ) : (
                        <span className="num">{pct(l.percent / 100, Number.isInteger(l.percent) ? 0 : 1)}</span>
                      )}
                    </td>
                    <td className={clsx(td, 'num text-right')}>{money(l.earnedCents)}</td>
                    <td className={clsx(td, 'num text-right text-graphite')}>{money(l.previousCents)}</td>
                    <td className={clsx(td, 'num text-right font-semibold', l.currentCents < 0 && 'text-bad')}>{money(l.currentCents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-sheet-2 font-semibold">
                  <td className={td}>{t('common.total')}</td>
                  <td />
                  <td className={clsx(td, 'num text-right')}>{money(model.totals.feeCents)}</td>
                  <td />
                  <td className={clsx(td, 'num text-right')}>{money(model.totals.earnedCents)}</td>
                  <td className={clsx(td, 'num text-right')}>{money(model.totals.previousCents)}</td>
                  <td className={clsx(td, 'num text-right')}>{money(model.totals.currentCents)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          {dirty && <p className="px-4 py-2 text-xs text-graphite">{t('billing.recalcHint')}</p>}
        </Panel>

        <Panel
          title={t('billing.extras')}
          actions={
            editable && (
              <Button
                size="sm"
                icon={<Plus className="size-3.5" />}
                onClick={() => {
                  setExtras([...extras, { label: '', amountCents: 0 }]);
                  setDirty(true);
                }}
              >
                {t('billing.addExtra')}
              </Button>
            )
          }
        >
          <div className="flex flex-col gap-2">
            {extras.map((x, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <Input
                  className="min-w-56 flex-1"
                  disabled={!editable}
                  value={x.label}
                  onChange={(e) => {
                    setExtras(extras.map((y, j) => (j === i ? { ...y, label: e.target.value } : y)));
                    setDirty(true);
                  }}
                />
                <MoneyInput
                  className="w-40"
                  value={x.amountCents}
                  onChange={(c) => {
                    setExtras(extras.map((y, j) => (j === i ? { ...y, amountCents: c ?? 0 } : y)));
                    setDirty(true);
                  }}
                />
                {editable && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setExtras(extras.filter((_, j) => j !== i));
                      setDirty(true);
                    }}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                )}
              </div>
            ))}
            <dl className="num mt-2 ml-auto grid w-full max-w-sm grid-cols-2 gap-y-1 text-sm">
              <dt>{t('billing.current')}</dt>
              <dd className="text-right">{money(model.totals.currentCents)}</dd>
              <dt>{t('billing.extras')}</dt>
              <dd className="text-right">{money(model.totals.extrasCents)}</dd>
              <dt className="font-semibold">{t('billing.net')}</dt>
              <dd className="text-right font-semibold">{money(model.totals.netCents)}</dd>
              <dt>
                {t('billing.vat')} {model.header.vatPercent} %
              </dt>
              <dd className="text-right">{money(model.totals.vatCents)}</dd>
              <dt className="font-semibold">{t('billing.gross')}</dt>
              <dd className="text-right font-semibold">{money(model.totals.grossCents)}</dd>
            </dl>
          </div>
        </Panel>

        {(editable || model.payment) && (
          <Panel
            title={t('billing.payment.title')}
            actions={
              editable &&
              data.projectTerms && (
                <Button
                  size="sm"
                  onClick={() => {
                    setPayment(paymentFormOf(data.projectTerms));
                    setPayDirty(true);
                  }}
                >
                  {t('billing.payment.fromProject')}
                </Button>
              )
            }
          >
            <p className="mb-3 text-xs text-graphite">{editable ? t('billing.payment.intro') : t('billing.payment.frozen')}</p>
            {editable && !hasPaymentTerms(liveTerms) && !payDirty && <Notice tone="neutral" className="mb-3">{t('billing.payment.none')}</Notice>}
            <div className="grid gap-3 md:grid-cols-2">
              <PaymentFields
                value={payment}
                disabled={!editable}
                onChange={(patch) => {
                  setPayment({ ...payment, ...patch });
                  setPayDirty(true);
                }}
              />
              {livePay && (
                <div className="rounded-md border border-line bg-sheet-2 p-3 md:col-span-2">
                  <div className="mb-2 text-sm font-semibold">{t('billing.payment.summary')}</div>
                  <PaymentBreakdownTable breakdown={livePay} vatPercent={header.vatPercent || 0} days={liveTerms.paymentDays} text={payDirty ? undefined : model.payment?.text} />
                  {(dirty || payDirty) && <p className="mt-2 text-xs text-graphite">{t('billing.payment.noteSaved')}</p>}
                </div>
              )}
            </div>
          </Panel>
        )}

        {model.previousDocuments.length > 0 && (
          <Panel title={t('billing.previousDocs')}>
            <ul className="flex flex-col gap-1 text-sm">
              {model.previousDocuments.map((p, i) => (
                <li key={i} className="num flex justify-between gap-4">
                  <span>
                    {p.number} <Pill tone={DOC_TONE[p.status]}>{t(`billing.docStatus.${p.status as 'paid'}`)}</Pill>
                  </span>
                  <span>{money(p.earnedCents)}</span>
                </li>
              ))}
            </ul>
          </Panel>
        )}
        {editable && (dirty || payDirty) && (
          <div className="flex justify-end">
            <Button variant="primary" icon={<Save className="size-4" />} loading={update.isPending} onClick={save}>
              {t('common.save')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
